import isElectron from 'is-electron';

import GeoLocationResource from 'flow/api/geolocation';
import { addPhoneLogEntry } from 'flow/utils/browser-utils';
import { IS_UAE_HOST } from 'flow/utils/browser-utils-TW';
import { isMacOs } from 'flow/utils/os-utils-flow';

export const GEO_LOCATION_HEADER = 'X-TVX-GPS-LOCATION';

const BROWSER_POSITION_TIMEOUT_MS = 15000;
const DESKTOP_POSITION_TIMEOUT_MS = 5000;

const MACOS_LOCATION_SETTINGS_URL =
    'x-apple.systempreferences:com.apple.preference.security?Privacy_LocationServices';
const WINDOWS_LOCATION_SETTINGS_URL = 'ms-settings:privacy-location';

const positionTimeout = () =>
    isElectron() ? DESKTOP_POSITION_TIMEOUT_MS : BROWSER_POSITION_TIMEOUT_MS;

export const GeoLocationPermission = Object.freeze({
    GRANTED: 'granted',
    PROMPT: 'prompt',
    DENIED: 'denied',
} as const);

export type GeoLocationPermissionType =
    (typeof GeoLocationPermission)[keyof typeof GeoLocationPermission];

export const watchGeoLocationPermissionState = (
    onChange: (state: GeoLocationPermissionType) => void,
): (() => void) => {
    if (!navigator.permissions?.query) {
        return () => {};
    }

    let status: PermissionStatus | null = null;
    const handleChange = () => {
        if (status) {
            onChange(status.state as GeoLocationPermissionType);
        }
    };

    navigator.permissions
        .query({ name: 'geolocation' })
        .then((permissionStatus) => {
            status = permissionStatus;
            permissionStatus.addEventListener('change', handleChange);
            onChange(permissionStatus.state as GeoLocationPermissionType);
        })
        .catch(() => {});

    return () => status?.removeEventListener('change', handleChange);
};

/**
 * Call back when the permission becomes granted, having not been.
 *
 * Built on {@link watchGeoLocationPermissionState} so there is one subscription idiom and one place
 * to fix when the next permission quirk turns up. The first state it sees only establishes where we
 * are starting from: at boot the caller has already asked for a position, and treating an initial
 * `granted` as a transition would ask again and act on a user agent that has not been rebuilt yet.
 */
export const onGeoLocationPermissionGranted = (
    onGranted: () => void,
): (() => void) =>
    ((seen: GeoLocationPermissionType | null) =>
        watchGeoLocationPermissionState((state) => {
            const becameGranted =
                seen !== null &&
                seen !== GeoLocationPermission.GRANTED &&
                state === GeoLocationPermission.GRANTED;

            seen = state;

            if (becameGranted) {
                onGranted();
            }
        }))(null);

export const canOpenLocationSettings = () => isElectron();

export const openLocationSettings = () => {
    window.open(
        isMacOs() ? MACOS_LOCATION_SETTINGS_URL : WINDOWS_LOCATION_SETTINGS_URL,
    );
};

export const GEO_FIX_MAX_AGE_MS = 60000;
export const GEO_FIX_WAIT_MS = 1500;
export const GEO_RETRY_INTERVAL_MS = 30000;

const GEO_CONFIG_TIMEOUT_MS = 5000;

export const GeoLocationStatus = Object.freeze({
    PENDING: 'PENDING',
    AVAILABLE: 'AVAILABLE',
    BLOCKED: 'BLOCKED',
} as const);

export type GeoLocationStatusType =
    (typeof GeoLocationStatus)[keyof typeof GeoLocationStatus];

export type GeoLocationCoordinates = {
    latitude: number;
    longitude: number;
};

export type GeoLocationResult = {
    coordinates: GeoLocationCoordinates | null;
    permissionDenied: boolean;
};

export const geoRestrictionIsDeployed = () => IS_UAE_HOST;

export const geoRestrictionIsEnabled = (enabled: boolean | null | undefined) =>
    enabled === true;

export const geoRestrictionMayBeEnabled = (
    enabled: boolean | null | undefined,
) => enabled !== false;

export const formatGeoLocationHeaderValue = ({
    latitude,
    longitude,
}: GeoLocationCoordinates) => `${latitude},${longitude}`;

export const geoLocationExtraHeader = (coordinates: GeoLocationCoordinates) =>
    `${GEO_LOCATION_HEADER}: ${formatGeoLocationHeaderValue(coordinates)}`;

export const requestGeoLocation = (
    timeoutMs = positionTimeout(),
    onLateFix?: (coordinates: GeoLocationCoordinates) => void,
): Promise<GeoLocationResult> => {
    if (!navigator.geolocation) {
        addPhoneLogEntry(
            'Geo location: navigator.geolocation is unavailable, cannot report a position',
        );
        return Promise.resolve({ coordinates: null, permissionDenied: true });
    }

    return new Promise((resolve) => {
        let gaveUp = false;
        const giveUpTimeoutId = setTimeout(() => {
            gaveUp = true;
            addPhoneLogEntry(
                'Geo location: no answer to the permission prompt, giving up on a position for now',
            );
            resolve({ coordinates: null, permissionDenied: false });
        }, timeoutMs);

        navigator.geolocation.getCurrentPosition(
            ({ coords }) => {
                clearTimeout(giveUpTimeoutId);
                const coordinates = {
                    latitude: coords.latitude,
                    longitude: coords.longitude,
                };

                if (gaveUp) {
                    addPhoneLogEntry(
                        'Geo location: position arrived after we gave up waiting, taking it anyway',
                    );
                    onLateFix?.(coordinates);
                    return;
                }

                addPhoneLogEntry('Geo location: position resolved');
                resolve({ coordinates, permissionDenied: false });
            },
            (error) => {
                clearTimeout(giveUpTimeoutId);
                addPhoneLogEntry(
                    `Geo location: position unavailable, code ${error.code}: ${error.message}`,
                );

                if (gaveUp) {
                    return;
                }

                resolve({
                    coordinates: null,
                    permissionDenied: error.code === error.PERMISSION_DENIED,
                });
            },
            { timeout: timeoutMs },
        );
    });
};

export const checkGeoRestrictionIsEnabled = (): Promise<boolean | null> =>
    new Promise((resolve) => {
        let settled = false;

        const settle = (enabled: boolean | null, logEntry: string) => {
            if (settled) {
                return;
            }

            settled = true;
            clearTimeout(timeoutId);
            addPhoneLogEntry(logEntry);
            resolve(enabled);
        };

        const timeoutId = setTimeout(
            () =>
                settle(
                    null,
                    'Geo location: switch timed out, reporting the position without blocking',
                ),
            GEO_CONFIG_TIMEOUT_MS,
        );

        const onFailure = (request: XMLHttpRequest) =>
            settle(
                null,
                `Geo location: switch answered ${request?.status}, reporting the position without blocking`,
            );

        GeoLocationResource.getConfig({
            onResult: (config: { enabled?: unknown } | string) => {
                const enabled =
                    typeof config === 'object' &&
                    typeof config?.enabled === 'boolean'
                        ? config.enabled
                        : null;

                settle(
                    enabled,
                    `Geo location: region restriction switch answered ${enabled}`,
                );
            },
            onClientError: onFailure,
            onError: onFailure,
        });
    });
